'use client';

import React, {
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { AnimationItem } from 'lottie-web';
import { useLottieAnimation } from 'hooks/animation/useLottieAnimation';
import { API_BASE_URL } from 'lib/config/apiEndpoints';
import { emailRegex } from 'utils/data';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import { CaretUp, InfoIcon } from 'assets/icons';
import { useGlobalUI } from 'contexts/GlobalUIContext';
import styles from './redeem-client.module.css';

import { getAssetUrl } from 'lib/config/assetUrls';

const REDEEM_LOTTIE = getAssetUrl('/web_v2/site-static/lottie/redeem.json');

interface Country {
  id: number;
  name: string;
  iso2: string;
  country_code: string;
  phone_code: string;
}

const RedeemCard = ({ className }: { className?: string }) => {
  useGlobalUI({ liteUI: true });

  const [screen, setScreen] = useState<'form' | 'success' | 'error'>('form');

  const [countries, setCountries] = useState<Country[]>([]);
  const [redeemMessage, setRedeemMessage] = useState<string>('');
  const [uhxCode, setUhxCode] = useState('');
  const [email, setEmail] = useState<string>();
  const [country, setCountry] = useState<Country>();

  const [emailConfirm, setEmailConfirm] = useState<string>();

  const location = useRegionLocale();

  const [formLoading] = useState(false);

  const submitEnabled =
    uhxCode &&
    email &&
    email.match(emailRegex) &&
    emailConfirm &&
    email === emailConfirm &&
    country &&
    !formLoading;

  useEffect(() => {
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    try {
      const response = await fetch(
        'https://public-assets.uh-static.com/country-state-city/countries.json',
      );
      const data = await response.json();
      console.log({ data });
      setCountries(data);
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const handleCountryChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedOption = event.target.value;

    const selectedCountry = countries.find(
      (country) => country.name === selectedOption,
    );

    if (selectedCountry) {
      setCountry(selectedCountry);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setRedeemMessage('');

    const bodyData = {
      purchase_code: uhxCode,
      email,
      region: country?.iso2,
    };

    console.log(bodyData);

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/web_v1/uhx_purchase_code`,
        {
          method: 'POST',
          body: JSON.stringify(bodyData),
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );
      const data = await response.json();
      if (data.success) {
        setRedeemMessage(data.message);
        setScreen('success');
      } else {
        setRedeemMessage(data.message);
      }
      console.log({ data });
    } catch (error) {
      console.error('Error fetching states:', error);
      setRedeemMessage('Error redeeming code. Please try again later.');
    }
  };

  useEffect(() => {
    const selectedCountry = countries.find(
      (country) =>
        country.iso2.toLowerCase() === location.region?.toLowerCase(),
    );

    if (selectedCountry) {
      setCountry(selectedCountry);
    }
  }, [location, countries]);

  const Form = (
    <form>
      <div className={styles.form}>
        <div className={styles['text-description']}>
          <h1>Activate your UltrahumanX membership</h1>
          <p>
            If you purchased your UltrahumanX membership card at a retail store,
            you can activate it for your region by using the purchase code
            printed on your card.
          </p>
        </div>
        <div className={`contact-details ${styles.block}`}>
          {/* <p>UHX HEAD</p> */}
          <div className={styles['input-rows']}>
            <label>
              <span className={styles['lable-title']}>Purchase Code</span>
              <input
                type="text"
                name="uhxCode"
                placeholder="XXYYZZ"
                required
                value={uhxCode}
                onChange={(e) => setUhxCode(e.target.value)}
              />
            </label>
            <label>
              <span className={styles['lable-title']}>Email</span>
              <input
                type="email"
                name="email"
                placeholder="john@example.com"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </label>

            <label>
              <span className={styles['lable-title']}>Confirm Email</span>
              <input
                type="email"
                name="email-confirm"
                placeholder="john@example.com"
                required
                value={emailConfirm}
                onChange={(e) => setEmailConfirm(e.target.value)}
              />
            </label>

            <label>
              <span className={styles['lable-title']}>Country</span>
              <div className={styles['select-wrapper']}>
                <select
                  id="country"
                  value={
                    country?.name ||
                    (location?.countryName ? location?.countryName : '')
                  }
                  onChange={handleCountryChange}
                  required
                >
                  <option value="">Select a country</option>
                  {countries.map((country) => (
                    <option key={country.iso2} value={country.name}>
                      {country.name}
                    </option>
                  ))}
                </select>
                <CaretUp
                  style={{
                    transform: 'rotate(180deg) translateY(50%)',
                    position: 'absolute',
                    top: '50%',
                    right: '12px',
                    pointerEvents: 'none',
                  }}
                />
              </div>
            </label>
          </div>
        </div>
        <div className={styles['info-error-container']}>
          {redeemMessage && (
            <p>
              {' '}
              <InfoIcon
                fill="rgba(255,255,255,0.5)"
                style={{ marginRight: '4px' }}
              />{' '}
              {redeemMessage}
            </p>
          )}
        </div>
        <button
          className={styles.submit}
          type="submit"
          onClick={handleSubmit}
          disabled={!submitEnabled}
        >
          Activate membership
        </button>
      </div>
    </form>
  );

  const Success = (
    <div className={styles['success-block']}>
      <h2>UltrahumanX activated</h2>
      <p>
        Congratulations! Your UltrahumanX membership has been successfully
        activated.
      </p>
    </div>
  );

  return (
    <div
      className={
        className ? `${styles.redeemCard} ${className}` : styles.redeemCard
      }
    >
      <Layout>
        {screen === 'form' ? Form : null}
        {screen === 'success' ? Success : null}
      </Layout>
    </div>
  );
};

export default RedeemCard;

export const Layout = ({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) => {
  const animContainerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<AnimationItem | null>(null);

  useLottieAnimation(animContainerRef, {
    path: REDEEM_LOTTIE,
    renderer: 'svg',
    loop: true,
    autoplay: true,
    onInstance: (animation: AnimationItem) => {
      animationRef.current = animation;
      if (animationRef.current.isPaused) {
        animationRef.current.play();
      }
    },
  });

  const handleContact = () => {
    if (window && document != undefined) {
      // const message = encodeURIComponent(
      //   'Hello, I need some help in ordering my Ultrahuman Ring.',
      // );
      window.open(`mailto:support@ultrahuman.com?`, '_blank');
    }
  };

  return (
    <div
      className={className ? `${styles.layout} ${className}` : styles.layout}
    >
      <div ref={animContainerRef} className={styles.lottie} />
      <div className={styles.content}>{children}</div>
      <div className={styles.footer}>
        <p>
          Any issues? <button onClick={handleContact}>Email support</button>{' '}
          at support@ultrahuman.com
        </p>
      </div>
    </div>
  );
};
